import { useEffect, useRef, useState } from "react";
import type { NavigateFunction } from "react-router-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Alert } from "../components/ui/Alert";
import { SpinnerOverlay } from "../components/ui/Spinner";
import type { useAcceptInvite } from "../hooks/api/useInvites";
import { useAcceptInvite as useAcceptInviteHook } from "../hooks/api/useInvites";
import type { User } from "../stores/authStore";
import { useAuthStore } from "../stores/authStore";

function parseJwtPayload(token: string): Record<string, unknown> {
  const parts = token.split(".");
  if (parts.length !== 3 || !parts[1]) {
    throw new Error("Invalid token format");
  }
  const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return JSON.parse(new TextDecoder().decode(bytes)) as Record<string, unknown>;
}

function handleInviteFlow(
  params: {
    state: string;
    email: string | null;
    name: string | null;
  },
  acceptInvite: ReturnType<typeof useAcceptInvite>,
  setStatus: (s: string) => void,
  setError: (s: string) => void,
  navigate: NavigateFunction
) {
  const inviteToken = params.state.replace("invite:", "");

  if (!(params.email && params.name)) {
    setError("ユーザー情報が取得できませんでした");
    setTimeout(() => {
      navigate("/auth/login?error=invalid_token", { replace: true });
    }, 2000);
    return;
  }

  setStatus("アカウントを作成中...");

  acceptInvite.mutate(
    { token: inviteToken, email: params.email, name: params.name },
    {
      onSuccess: () => {
        navigate("/seats", { replace: true });
      },
      onError: (err) => {
        setError(
          err instanceof Error ? err.message : "アカウントの作成に失敗しました"
        );
        setTimeout(() => {
          navigate("/auth/login?error=registration_failed", {
            replace: true,
          });
        }, 2000);
      },
    }
  );
}

function handleTokenFlow(
  accessToken: string,
  refreshToken: string,
  login: (user: User, token: string, refreshToken: string) => void,
  navigate: NavigateFunction,
  setError: (s: string) => void
) {
  try {
    const payload = parseJwtPayload(accessToken);
    const email = payload.email as string;
    const user: User = {
      email,
      name: (payload.name as string) || email.split("@")[0] || email,
      picture: payload.picture as string | undefined,
      isAdmin: payload.role === "admin",
    };

    login(user, accessToken, refreshToken);
    navigate("/seats", { replace: true });
  } catch {
    setError("トークンの解析に失敗しました");
    setTimeout(() => {
      navigate("/auth/login?error=invalid_token", { replace: true });
    }, 2000);
  }
}

export function AuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuthStore();
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string>("ログイン処理中...");
  const acceptInvite = useAcceptInviteHook();
  const processedRef = useRef(false);

  useEffect(() => {
    if (processedRef.current) {
      return;
    }
    processedRef.current = true;

    const accessToken = searchParams.get("access_token");
    const refreshToken = searchParams.get("refresh_token");
    const errorParam = searchParams.get("error");
    const state = searchParams.get("state");
    const email = searchParams.get("email");
    const name = searchParams.get("name");

    if (errorParam) {
      navigate(`/auth/login?error=${errorParam}`, { replace: true });
      return;
    }

    const isInviteFlow = state?.startsWith("invite:");

    if (isInviteFlow && state) {
      handleInviteFlow(
        { state, email, name },
        acceptInvite,
        setStatus,
        setError,
        navigate
      );
      return;
    }

    if (!(accessToken && refreshToken)) {
      navigate("/auth/login?error=invalid_token", { replace: true });
      return;
    }

    handleTokenFlow(accessToken, refreshToken, login, navigate, setError);
  }, [searchParams, login, navigate, acceptInvite]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100">
        <div className="flex flex-col items-center gap-4">
          <Alert variant="error">{error}</Alert>
        </div>
      </div>
    );
  }

  return <SpinnerOverlay message={status} />;
}
